import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { memoryStore } from "../stores/MemoryStore";
import {
  deleteMyCoachMemory,
  listMyCoachMemoryItems,
  deleteMyCoachMemoryItem,
  exportMyCoachMemory,
} from "../queries/userMemory";

const MIN_HEIGHT_PX = 80;
const MAX_HEIGHT_VH = 0.85;
const DEFAULT_HEIGHT_PX = 180;

const TABS = [
  { key: "reflect", label: "Reflect" },
  { key: "recalls", label: "Recall" },
  { key: "retains", label: "Retain" },
  { key: "items",   label: "Items" },
];

// ----- color tokens -----

const C = {
  bg: "#0f172a",
  bgAlt: "#1e293b",
  panel: "#0b1220",
  border: "#334155",
  borderSoft: "#1e293b",
  text: "#e2e8f0",
  textDim: "#94a3b8",
  textMute: "#64748b",
  accent: "#60a5fa",
  accentBg: "#1e3a8a",
  user: "#fbbf24",
  assistant: "#34d399",
  tool: "#a78bfa",
  danger: "#f87171",
  dangerBg: "#7f1d1d",
};

// ----- formatting helpers -----

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
};

const roleColor = (role) => {
  if (role === "user") return C.user;
  if (role === "assistant") return C.assistant;
  if (role === "tool_summary" || role === "tool") return C.tool;
  return C.textDim;
};

// Truncate long strings for at-a-glance display
const truncate = (s, n = 240) => {
  if (typeof s !== "string") return String(s ?? "");
  return s.length > n ? s.slice(0, n) + "…" : s;
};

// ----- shared atoms -----

const Pill = ({ children, color = C.textDim, bg = "transparent" }) => (
  <span style={{
    display: "inline-block",
    padding: "1px 6px",
    fontSize: "10px",
    fontWeight: 600,
    color,
    background: bg,
    border: `1px solid ${color}`,
    borderRadius: "3px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  }}>{children}</span>
);

const KV = ({ k, v }) => (
  <div style={{ display: "flex", gap: "8px", fontSize: "11px", lineHeight: "1.6" }}>
    <span style={{ color: C.textMute, minWidth: "80px" }}>{k}</span>
    <span style={{ color: C.text }}>{v}</span>
  </div>
);

const Empty = ({ children }) => (
  <div style={{ color: C.textMute, fontStyle: "italic", padding: "16px" }}>{children}</div>
);

// ----- per-item renderers -----

const MemoryItemCard = ({ item }) => {
  const role = item?.metadata?.role || item?.role || "?";
  const agent = item?.metadata?.agent;
  const action = item?.metadata?.agent_action;
  const turn = item?.metadata?.turn_index;
  return (
    <div style={{
      padding: "8px 10px",
      marginBottom: "6px",
      background: C.panel,
      border: `1px solid ${C.borderSoft}`,
      borderLeft: `3px solid ${roleColor(role)}`,
      borderRadius: "3px",
    }}>
      <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "4px" }}>
        <Pill color={roleColor(role)}>{role}</Pill>
        {agent && <span style={{ color: C.textDim, fontSize: "10px" }}>{agent}</span>}
        {action && action !== role && (
          <span style={{ color: C.textMute, fontSize: "10px" }}>· {action}</span>
        )}
        {turn !== undefined && (
          <span style={{ color: C.textMute, fontSize: "10px", marginLeft: "auto" }}>turn {turn}</span>
        )}
        {item.timestamp && (
          <span style={{ color: C.textMute, fontSize: "10px" }}>{fmtTime(item.timestamp)}</span>
        )}
      </div>
      <div style={{ color: C.text, fontSize: "12px", lineHeight: "1.5", whiteSpace: "pre-wrap" }}>
        {truncate(item.content, 600)}
      </div>
      {item.tags?.length > 0 && (
        <div style={{ marginTop: "4px", display: "flex", gap: "4px", flexWrap: "wrap" }}>
          {item.tags.map((t, i) => (
            <span key={i} style={{
              fontSize: "9px",
              color: C.textDim,
              background: C.bgAlt,
              padding: "1px 5px",
              borderRadius: "2px",
            }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
};

// ----- tab views -----

const ReflectView = observer(() => {
  const r = memoryStore.reflect;
  if (!r) return <Empty>(no reflect yet — first-turn reflect runs in the background and arrives async)</Empty>;

  const out = r.output || {};
  const sections = [
    { key: "topics",      label: "Topics",      items: out.topics },
    { key: "entities",    label: "Entities",    items: out.entities },
    { key: "threads",     label: "Threads",     items: out.threads },
    { key: "decisions",   label: "Decisions",   items: out.decisions },
    { key: "preferences", label: "Preferences", items: out.preferences },
  ].filter(s => Array.isArray(s.items) && s.items.length > 0);

  if (sections.length === 0) {
    return <Empty>(reflect returned an empty structure — no continuity from prior sessions yet)</Empty>;
  }

  return (
    <div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "10px", color: C.textMute, fontSize: "10px" }}>
        <span>latency: {r.latency_ms ?? "?"}ms</span>
        <span>·</span>
        <span>at: {fmtTime(new Date(r.ts).toISOString())}</span>
      </div>
      {sections.map(s => (
        <div key={s.key} style={{ marginBottom: "12px" }}>
          <div style={{ color: C.accent, fontSize: "11px", fontWeight: 600, marginBottom: "4px" }}>
            {s.label} ({s.items.length})
          </div>
          {s.items.map((item, i) => (
            <div key={i} style={{
              padding: "5px 8px",
              marginBottom: "3px",
              background: C.panel,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: "3px",
              fontSize: "11px",
              color: C.text,
            }}>
              {typeof item === "string" ? item : (
                <div>
                  {item.name && <strong style={{ color: C.text }}>{item.name}</strong>}
                  {item.anchor && <strong style={{ color: C.text }}>{item.anchor}</strong>}
                  {item.plex_id && <span style={{ color: C.textMute, marginLeft: "6px" }}>{item.plex_id}</span>}
                  {item.category && <Pill color={C.textDim} bg={C.bgAlt}>{item.category}</Pill>}
                  {item.summary && <div style={{ color: C.textDim, marginTop: "2px" }}>{item.summary}</div>}
                  {item.last_active && <span style={{ color: C.textMute, fontSize: "10px" }}>last active: {item.last_active}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
});

const RecallsView = observer(() => {
  if (memoryStore.recalls.length === 0) {
    return <Empty>(no recalls yet — recall runs on turn 2+)</Empty>;
  }
  return (
    <div>
      {memoryStore.recalls.map((r, i) => (
        <div key={i} style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "4px" }}>
            <Pill color={C.accent} bg={C.accentBg}>query</Pill>
            <span style={{ color: C.text, fontSize: "12px", flex: 1 }}>{truncate(r.query, 120)}</span>
            <span style={{ color: C.textMute, fontSize: "10px" }}>{r.latency_ms}ms</span>
            <span style={{ color: C.textMute, fontSize: "10px" }}>{fmtTime(new Date(r.ts).toISOString())}</span>
          </div>
          {r.items?.length > 0 ? (
            <div style={{ marginLeft: "12px" }}>
              {r.items.map((it, j) => (
                <div key={j} style={{
                  padding: "6px 8px",
                  marginBottom: "4px",
                  background: C.panel,
                  border: `1px solid ${C.borderSoft}`,
                  borderRadius: "3px",
                  fontSize: "11px",
                  color: C.text,
                  whiteSpace: "pre-wrap",
                }}>
                  {it.timestamp && (
                    <div style={{ color: C.textMute, fontSize: "10px", marginBottom: "2px" }}>
                      {fmtTime(it.timestamp)}
                    </div>
                  )}
                  {truncate(it.content, 400)}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ marginLeft: "12px", color: C.textMute, fontSize: "11px", fontStyle: "italic" }}>
              no items returned
            </div>
          )}
        </div>
      ))}
    </div>
  );
});

const RetainsView = observer(() => {
  if (memoryStore.retains.length === 0) {
    return <Empty>(no retains yet — items retain after each turn)</Empty>;
  }
  return (
    <div>
      {memoryStore.retains.map((r, i) => (
        <div key={i} style={{ marginBottom: "12px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "4px" }}>
            <Pill
              color={r.async_status === "queued" ? C.assistant : C.danger}
              bg={r.async_status === "queued" ? "#064e3b" : C.dangerBg}
            >
              {r.async_status}
            </Pill>
            <span style={{ color: C.text, fontSize: "11px" }}>{r.items?.length || 0} items</span>
            {r.operation_id && (
              <span style={{ color: C.textMute, fontSize: "10px" }}>op: {r.operation_id.slice(0, 8)}</span>
            )}
            <span style={{ color: C.textMute, fontSize: "10px", marginLeft: "auto" }}>
              {fmtTime(new Date(r.ts).toISOString())}
            </span>
          </div>
          <div style={{ marginLeft: "12px" }}>
            {(r.items || []).map((item, j) => (
              <MemoryItemCard key={j} item={item} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

// ItemsView fetches the persisted memory items from Hindsight and lets
// the user surgically delete one at a time. Unlike Retain (which buffers
// what just happened in this browser session), Items shows the bank's
// actual contents.
const ItemsView = ({ userStore }) => {
  const [items, setItems] = useState(null);    // null = not yet fetched
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const refresh = async () => {
    setLoading(true); setError(null);
    try {
      const result = await listMyCoachMemoryItems(userStore.authData);
      // Hindsight returns {items, total, limit, offset}
      setItems(result?.items || result?.memories || []);
    } catch (err) {
      setError(`Load failed: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-line */ }, []);

  const handleDelete = async (memoryId) => {
    if (!window.confirm("Delete this memory item?")) return;
    setDeletingId(memoryId);
    try {
      await deleteMyCoachMemoryItem(userStore.authData, memoryId);
      setItems(prev => prev.filter(it => (it.id ?? it.memory_id) !== memoryId));
    } catch (err) {
      setError(`Delete failed: ${err?.message || err}`);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && items === null) return <Empty>(loading…)</Empty>;
  if (error) return (
    <div>
      <Empty>{error}</Empty>
      <button onClick={refresh} style={{
        margin: "8px 16px", padding: "4px 10px", fontSize: "11px",
        color: C.textDim, background: "transparent",
        border: `1px solid ${C.border}`, borderRadius: "3px",
        cursor: "pointer", fontFamily: "inherit",
      }}>retry</button>
    </div>
  );
  if (!items || items.length === 0) return <Empty>(no items in bank yet)</Empty>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
        <span style={{ color: C.textMute, fontSize: "11px" }}>{items.length} items</span>
        <button onClick={refresh} style={{
          padding: "2px 8px", fontSize: "10px",
          color: C.textDim, background: "transparent",
          border: `1px solid ${C.border}`, borderRadius: "3px",
          cursor: "pointer", fontFamily: "inherit",
        }}>refresh</button>
      </div>
      {items.map((it, i) => {
        const id = it.id ?? it.memory_id ?? it.document_id;
        const meta = it.document_metadata || it.retain_params?.metadata || {};
        const role = meta.role || "?";
        return (
          <div key={id ?? i} style={{
            padding: "8px 10px",
            marginBottom: "6px",
            background: C.panel,
            border: `1px solid ${C.borderSoft}`,
            borderLeft: `3px solid ${roleColor(role)}`,
            borderRadius: "3px",
            display: "flex",
            gap: "8px",
            alignItems: "flex-start",
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "4px" }}>
                <Pill color={roleColor(role)}>{role}</Pill>
                {meta.agent && <span style={{ color: C.textDim, fontSize: "10px" }}>{meta.agent}</span>}
                {meta.turn_index !== undefined && (
                  <span style={{ color: C.textMute, fontSize: "10px" }}>· turn {meta.turn_index}</span>
                )}
                <span style={{ color: C.textMute, fontSize: "10px", marginLeft: "auto" }}>
                  {it.text_length ?? "?"} chars
                </span>
                {it.created_at && (
                  <span style={{ color: C.textMute, fontSize: "10px" }}>{fmtTime(it.created_at)}</span>
                )}
              </div>
              {id && (
                <div style={{ color: C.textMute, fontSize: "10px", fontFamily: "inherit" }}>
                  id: {String(id).slice(0, 8)}{it.memory_unit_count !== undefined && ` · ${it.memory_unit_count} units`}
                </div>
              )}
            </div>
            <button
              disabled={deletingId === id}
              onClick={() => handleDelete(id)}
              title="Delete this item"
              style={{
                padding: "2px 8px", fontSize: "11px",
                color: C.danger, background: "transparent",
                border: `1px solid ${C.danger}`, borderRadius: "3px",
                cursor: deletingId === id ? "wait" : "pointer",
                fontFamily: "inherit",
                flexShrink: 0,
              }}>{deletingId === id ? "…" : "✕"}</button>
          </div>
        );
      })}
    </div>
  );
};

// ----- chrome -----

const tabStyle = (active) => ({
  padding: "8px 14px",
  cursor: "pointer",
  borderBottom: active ? `2px solid ${C.accent}` : "2px solid transparent",
  color: active ? C.accent : C.textDim,
  fontWeight: active ? 600 : 400,
  fontSize: "12px",
  letterSpacing: "0.02em",
});

const MemoryWindow = observer(({ userStore }) => {
  const [open, setOpen] = useState(localStorage.getItem("plex-mem-window") === "open");
  const [tab, setTab] = useState(localStorage.getItem("plex-mem-tab") || "reflect");

  const [height, setHeight] = useState(() => {
    const stored = parseInt(localStorage.getItem("plex-mem-height"), 10);
    return Number.isFinite(stored) && stored >= MIN_HEIGHT_PX ? stored : DEFAULT_HEIGHT_PX;
  });
  const dragRef = useRef(null);

  const onResizeStart = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = height;
    dragRef.current = { startY, startHeight };
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";

    const onMove = (ev) => {
      if (!dragRef.current) return;
      // Drawer is bottom-pinned, so dragging up grows the drawer.
      const delta = dragRef.current.startY - ev.clientY;
      const max = Math.round(window.innerHeight * MAX_HEIGHT_VH);
      const next = Math.min(max, Math.max(MIN_HEIGHT_PX, dragRef.current.startHeight + delta));
      setHeight(next);
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Tick once a second so the "Xs ago" relative time stays fresh.
  // Date.now() isn't observable, so without this the label freezes
  // at the value it had when memoryStore.lastEventAt last changed.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!open || !memoryStore.lastEventAt) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [open, memoryStore.lastEventAt]);

  // Persist height (debounced via effect — only commits the last value
  // after a drag settles, not on every pixel of motion).
  useEffect(() => {
    const t = setTimeout(() => {
      localStorage.setItem("plex-mem-height", String(height));
    }, 200);
    return () => clearTimeout(t);
  }, [height]);

  // Publish the drawer's footprint as a CSS variable so the chat-session
  // can leave room for it (otherwise the bottom of the conversation gets
  // hidden behind this fixed-position panel).
  useEffect(() => {
    const COLLAPSED_HEADER_PX = 26;
    const total = open ? COLLAPSED_HEADER_PX + height : COLLAPSED_HEADER_PX;
    document.documentElement.style.setProperty("--memory-window-h", `${total}px`);
    return () => {
      document.documentElement.style.removeProperty("--memory-window-h");
    };
  }, [open, height]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem("plex-mem-window", next ? "open" : "closed");
  };

  const setActiveTab = (key) => {
    setTab(key);
    localStorage.setItem("plex-mem-tab", key);
  };

  const lastActivity = memoryStore.lastEventAt
    ? `${Math.round((Date.now() - memoryStore.lastEventAt) / 1000)}s ago`
    : "never";

  const handleDelete = async () => {
    if (!window.confirm("Delete your coach memory? This cannot be undone.")) return;
    try {
      await deleteMyCoachMemory(userStore.authData);
      memoryStore.clear();
    } catch (err) {
      console.warn("Memory delete failed", err);
    }
  };

  const handleExport = async () => {
    try {
      const resp = await exportMyCoachMemory(userStore.authData);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      // Pull the server-suggested filename out of Content-Disposition
      const cd = resp.headers.get("content-disposition") || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : "coach-memory.json";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("Memory export failed", err);
      window.alert(`Export failed: ${err?.message || err}`);
    }
  };

  const counts = {
    reflect: memoryStore.reflect ? 1 : 0,
    recalls: memoryStore.recalls.length,
    retains: memoryStore.retains.length,
  };

  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0,
      background: C.bg,
      borderTop: `1px solid ${C.border}`,
      zIndex: 100,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      color: C.text,
      boxShadow: open ? "0 -4px 12px rgba(0,0,0,0.4)" : "none",
    }}>
      <div onClick={toggle} style={{
        padding: "3px 14px",
        cursor: "pointer",
        display: "flex",
        gap: "10px",
        alignItems: "center",
        background: C.bgAlt,
        fontSize: "10.5px",
        userSelect: "none",
        lineHeight: 1.6,
      }}>
        <span style={{ fontSize: "11px" }}>{"🧠"}</span>
        <span style={{ color: C.textDim }}>
          {memoryStore.bankIdPrefix || (
            <span style={{ fontStyle: "italic", color: C.textMute }}>(no bank yet)</span>
          )}
        </span>
        <span style={{ color: C.textMute }}>·</span>
        <span><strong style={{ color: C.text }}>{memoryStore.itemCount}</strong> <span style={{ color: C.textMute }}>items</span></span>
        <span style={{ color: C.textMute }}>·</span>
        <span style={{ color: C.textMute }}>last event {lastActivity}</span>
        <span style={{ marginLeft: "auto", color: C.textDim, fontSize: "9px" }}>{open ? "▼" : "▲"}</span>
      </div>

      {open && (
        <div style={{ height: `${height}px`, display: "flex", flexDirection: "column" }}>
          <div
            onMouseDown={onResizeStart}
            title="Drag to resize"
            style={{
              height: "6px",
              cursor: "ns-resize",
              background: C.bgAlt,
              borderTop: `1px solid ${C.border}`,
              borderBottom: `1px solid ${C.borderSoft}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <div style={{
              width: "32px",
              height: "2px",
              borderRadius: "1px",
              background: C.textMute,
              opacity: 0.6,
            }} />
          </div>
          <div style={{
            display: "flex",
            borderBottom: `1px solid ${C.border}`,
            padding: "0 16px",
            alignItems: "center",
            justifyContent: "space-between",
            background: C.bgAlt,
          }}>
            <div style={{ display: "flex" }}>
              {TABS.map((t) => (
                <div key={t.key} onClick={() => setActiveTab(t.key)} style={tabStyle(tab === t.key)}>
                  {t.label}
                  {counts[t.key] > 0 && (
                    <span style={{
                      marginLeft: "6px",
                      fontSize: "10px",
                      color: tab === t.key ? C.accent : C.textMute,
                    }}>
                      {counts[t.key]}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              <button onClick={handleExport} title="Download bank as JSON" style={{
                padding: "4px 10px",
                fontSize: "11px",
                cursor: "pointer",
                color: C.textDim,
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: "3px",
                fontFamily: "inherit",
              }}>export</button>
              <button onClick={handleDelete} style={{
                padding: "4px 10px",
                fontSize: "11px",
                cursor: "pointer",
                color: C.danger,
                background: "transparent",
                border: `1px solid ${C.danger}`,
                borderRadius: "3px",
                fontFamily: "inherit",
              }}>delete my memory</button>
            </div>
          </div>

          <div style={{ flex: 1, overflow: "auto", padding: "12px 16px", background: C.bg }}>
            {tab === "reflect" && <ReflectView />}
            {tab === "recalls" && <RecallsView />}
            {tab === "retains" && <RetainsView />}
            {tab === "items" && <ItemsView userStore={userStore} />}
          </div>
        </div>
      )}
    </div>
  );
});

export default MemoryWindow;
